import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SendType } from '../pages/home/compose/composeTypes'
import { toArray } from '../utils'

export enum URLAction {
  SetComposeIndividuals = 'individuals',
  SetComposeStudents = 'students',
  SetComposeRegardingStudent = 'regardingStudent',
  SetComposeTeachers = 'teachers',
  SetComposeGroups = 'groups',
  SetComposeSendType = 'sendType',
  OpenCompose = 'compose',
  OpenMessageUserConversation = 'messageUser',
  OpenMessageConversation = 'message',
  OpenInboxTab = 'inboxTab',
  OpenFeedback = 'feedback',
  OpenGeneralLexikeetCall = 'generalLexikeetCall',
  OpenAttendanceSettings = 'attendanceSettings',
  OpenDistrictSettings = 'districtSettings',
  OpenCallerIDConfig = 'callerIDConfig',
  OpenAbbreviationConfig = 'abbreviationConfig',
  OpenEditUser = 'editUser',
  OpenTutorial = 'tutorial',
  SetSearch = 'search',
  SetSearchUser = 'searchUser',
  SetAllSchoolMessages = 'allSchoolMessages',
}

const convertLegacyParams = (params: URLSearchParams) => {
  // the 'co' param is sent in login links so we need to handle it in our new site,
  // every other one was just used within the old site and does not need to be supported
  if (params.has('co')) {
    params.set(URLAction.OpenMessageUserConversation, params.get('co')!)
    params.delete('co')
  }
}

const processParams = (search: string) => {
  const params = new URLSearchParams(search)
  convertLegacyParams(params)
  for (const key of params.keys()) {
    if (params.get(key) === '') params.set(key, 'true')
  }
  return params
}

interface urlParamsContextType {
  pop: (key: URLAction) => string | undefined
  peek: (key: URLAction) => string | undefined
  has: (key: URLAction) => boolean
  set: (key: URLAction, value: string | number | boolean) => void
  hasAny: boolean
}

const urlParamsContext = React.createContext<urlParamsContextType | undefined>(undefined)
export const URLParamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { search } = useLocation()
  const navigate = useNavigate()

  const URLParams = React.useMemo(() => {
    const params = processParams(search)
    return {
      pop: (key: URLAction) => {
        let rv: string | undefined = undefined
        if (params.has(key)) {
          rv = params.get(key)!
          params.delete(key)
          navigate('?' + params.toString(), { replace: true })
        }
        return rv
      },
      peek: (key: URLAction) => {
        const v = params.get(key)
        return v === null ? undefined : v
      },
      has: (key: URLAction) => {
        return params.has(key)
      },
      set: (key: URLAction, value: string | number | boolean) => {
        if (value === undefined) params.delete(key)
        else params.set(key, encodeURIComponent(value))
        navigate('?' + params.toString(), { replace: true })
      },
      hasAny: params.size > 0,
    }
  }, [search])

  return <urlParamsContext.Provider value={URLParams}>{children}</urlParamsContext.Provider>
}

/**
 * A hook to get the URL parameters.
 * @returns The URL parameters object, which contains the following methods:
 * - `pop(key: URLAction)`: Removes the parameter with the given key and returns its value.
 * - `peek(key: URLAction)`: Returns the value of the parameter with the given key without removing it.
 * - `has(key: URLAction)`: Returns true if the parameter with the given key exists.
 * - `set(key: URLAction, value: string | number | boolean)`: Sets the parameter with the given key to the given value.
 * - `hasAny`: Returns true if any parameters exist.
 */
export const useURLParams = () => {
  const params = React.useContext(urlParamsContext)
  if (params) return params
  throw Error('Cannot use URLParams outside provider')
}

type actionValue = string | number | boolean
type URLActionMap =
  | Partial<Record<URLAction, actionValue | actionValue[] | undefined>>
  | {
      [URLAction.SetSearchUser]: {
        id: number
        name: string
      }
    }

/**
 * Get a link to the specific page with the given URL actions as query parameters.
 */
export const getDirectLink = (input: URLActionMap | URLAction[] | URLAction, from: string = '/') => {
  const mapping: URLActionMap = Array.isArray(input)
    ? Object.fromEntries(input.map((key) => [key, undefined]))
    : typeof input === 'string'
      ? { [input]: undefined }
      : input

  let link = from
  const queryParams = Object.entries(mapping)
    .map(([key, value]) => {
      const valueString = typeof value === 'object' ? JSON.stringify(value) : value
      if (value === undefined) return `${key}`
      return `${key}=${toArray(valueString).join(',')}`
    })
    .join('&')
  if (queryParams) {
    link += '?' + queryParams
  }
  return link
}

/**
 * Get a link to the current page with the given URL actions as query parameters.
 */
export const getRelativeLink = (input: URLActionMap | URLAction[] | URLAction) => getDirectLink(input, '')

/**
 * Get a link to the home page with the given compose parameters.
 * This is used to open the compose page with pre-filled parameters.
 * @param students - The ids of students to send the message to (for teachers)
 * @param regardingStudent - The id of a student to send the message regarding (for guardians)
 * @param teachers - The ids of teachers to send the message to (for students and guardians)
 * @param type - The type of message to send
 * @param individuals - The ids of individuals to send the message to (for teachers)
 */
export const getComposeLink = ({
  students,
  regardingStudent,
  teachers,
  type,
  individuals,
}: {
  students?: number | number[]
  regardingStudent?: number
  teachers?: number | number[]
  type?: SendType | SendType[]
  individuals?: number | number[]
}) => {
  const params: URLActionMap = { [URLAction.OpenCompose]: true }
  if (students !== undefined) params[URLAction.SetComposeStudents] = students
  if (regardingStudent !== undefined) params[URLAction.SetComposeRegardingStudent] = regardingStudent
  if (teachers !== undefined) params[URLAction.SetComposeTeachers] = teachers
  if (type !== undefined) params[URLAction.SetComposeSendType] = type
  if (individuals !== undefined) params[URLAction.SetComposeIndividuals] = individuals
  return getDirectLink(params)
}

export const allUrlParams = () => Object.fromEntries(new URLSearchParams(window.location.search))
